import type { CarProfile, EngineLoopSample } from './CarProfile';

const BASE = '/audio/porsche-911-gt3-cup';

/**
 * Porsche 911 GT3 Cup — Marc's cockpit mix, the engine that ships.
 *
 * THE VOICE IS THE LICENSED RECORDINGS: `samples.overlay` is the echo|collective "Porsche 911 GT3"
 * library (bought, licensed), rpm-matched and played at gain 1.5 — that is what the ear hears, along with
 * the starter, the shutoff, the ambience, the doors and the pit-box life, all from the same library.
 * Underneath it, at 0.65, sits a synthetic base built from a simulator bank: audible only as body under
 * the real thing, and the one layer that is not ours (see docs/APPSTORE.md).
 *
 * Loops are pitch-flattened to a fixed rpm and looped on whole engine cycles; recordedRpm is measured from
 * the harmonic ladder (firing order 3 → f = rpm/20). loopStart/loopEnd play the intro once, then the region.
 */
const loop = (id: string, file: string, recordedRpm: number, loopStart?: number, loopEnd?: number): EngineLoopSample => ({
  id,
  file,
  recordedRpm,
  loopStart,
  loopEnd,
});

const onLoops: EngineLoopSample[] = [
  // idle = 100 % VIDA REAL (Marc): the real idle recording, labeled at this car's idle rpm so it
  // plays at rate 1.0 when idling (label-lie only stretches the brief transition toward on_3960)
  loop('idle', 'real_idle', 1920),
  loop('on_3960', 'on_3960', 3960, 0.2389, 7.0506),
  loop('on_6360', 'on_6360', 6360, 0.3888, 8.2235),
  loop('on_7920', 'on_7920', 7920),
];

const offLoops: EngineLoopSample[] = [
  loop('off_idle', 'real_idle', 1920),
  loop('off_4800', 'off_4800', 4800),
  loop('off_7730', 'off_7730', 7730),
];

export const flatSixCup: CarProfile = {
  // THE TWO-BANK BUILD IS THE REFERENCE NOW (Marc: "el oficial pasa a ser el single file"). The car
  // people buy and drive is the bounce of it in src/cars/single/, which carries this car's own id -
  // so every purchase, grant, link and Stripe price keeps working - and its name. This one is not in
  // the line-up any more; it stays here because the bounce is rendered FROM it and imports it for
  // everything a bounce does not change.
  id: 'porsche-911-gt3-cup-2v',
  slug: '510-hp-flat-six-2v',
  name: '510 HP FLAT-SIX 2 VOICES',
  manufacturer: 'Cup racer · Germany',
  engineType: '4.0 L flat-six · 8,300 rpm',
  tagline: 'Cockpit perspective, real recordings.',
  photo: { src: '/cars/510-hp-flat-six.jpg?v=1', pos: '50% 50%', credit: '' },   // generated (Nano Banana, 2026-09-14, Marc's take): our own image, no photographer, no badge, no factory light signature
  realTopSpeedKmh: 285,   // 992 GT3 Cup: >285 km/h
  hud: { accent: '#d5001c', accent2: '#ff5a3d', style: 'cosworth', brand: '#c9a545' },
  available: true,
  adminOnly: false, // LIVE (Marc 2026-08-24)

  idleRpm: 1920,
  maxRpm: 9000,
  redlineRpm: 8300,
  // REAL 992 GT3 Cup gearing: 6th peaks at the car's 285 km/h limit. The Home top-speed selector
  // (200 → 285) scales this whole ladder down, so revs/audio in every gear follow the chosen limit.
  gearTopSpeedsKmh: [92, 132, 170, 208, 246, 285],

  // Race logic: upshift exactly at the limiter, downshift into the limiter while braking.
  shift: {
    autoUpshiftRpm: 9000,
    partThrottleUpshiftRpm: 9000,
    brakingDownshiftMarginRpm: 150,
    autoDownshiftRpm: 3800,
    shiftTimeMs: 100,
    minShiftIntervalMs: 300,
    blipStrength: 0.75,
  },
  engine: { inertiaUpRpmPerS: 16000, inertiaDownRpmPerS: 12000, freeRevUpRpmPerS: 9500, freeRevDownRpmPerS: 5500 },

  audio: {
    // 1.15, from 0.9 (Marc, in the Tesla: 'el master volume, súbelo un poco'); the compressor holds the peaks
    masterGain: 1.15,
    onMixFloor: 0.2,
    liftOffLowpassHz: 3000,
    envelopeTauS: 0.035,
    procedural: { cylinders: 6, brightness: 0.7, roughness: 0.3, noise: 0.25 },
  },

  samples: {
    basePath: BASE,
    on: onLoops,
    off: offLoops,
    limiter: { id: 'limiter', file: 'limiter' },
    shiftUp: [{ id: 'shift_up_1', file: 'shift_up_1' }],
    shiftDown: [{ id: 'shift_down_1', file: 'shift_down_1' }],
    backfire: Array.from({ length: 4 }, (_, i) => ({ id: `backfire_${i + 1}`, file: `backfire_${i + 1}` })),
    wind: { id: 'wind', file: 'wind' },
    // debris (stones rattling the arches) removed at Marc's request: on the road it read as noise, not detail
    // ENG START / STOP / box: REAL 911 GT3 recordings (echo|collective library, licensed):
    // starter = crank + catch + settle (2.6 s — Marc kept the SHORT one; the long Run_08 start was tried in v211 and reverted); shutoff, 90 s garage ambience; doors for the CAN layer
    // OVERLAY experiment (Marc): the REAL recordings ladder layered rpm-matched over the RSR base
    overlay: {
      gain: 1.5,     // REAL a tope — Marc's blend (slider screenshot 2026-08-24)
      baseGain: 0.65, // AMS2 under it
      on: [
        // NOTHING derived from the Run_08 take belongs in this ladder. Three separate attempts to put it
        // here — raw loops, constant-pitch warped loops, and resynthesised engine orders — all came back
        // from the car as the same verdict: a siren. Measurements said each one should have worked; the
        // ear said otherwise, and the ear is the customer. Do not try a fourth without new material.
        loop('r_2000', 'real_on_2000', 2000), loop('r_2500', 'real_on_2500', 2500),
        loop('r_3000', 'real_on_3000', 3000), loop('r_3600', 'real_on_3600', 3600), loop('r_4200', 'real_on_4200', 4200),
        loop('r_4800', 'real_on_4800', 4800), loop('r_5400', 'real_on_5400', 5400), loop('r_6000', 'real_on_6000', 6000),
        loop('r_6600', 'real_on_6600', 6600), loop('r_7200', 'real_on_7200', 7200), loop('r_7800', 'real_on_7800', 7800),
        loop('r_8400', 'real_on_8400', 8400), loop('r_9000', 'real_on_9000', 9000),
      ],
      off: [
        loop('ro_2000', 'real_off_2000', 2000), loop('ro_2700', 'real_off_2700', 2700),
        loop('ro_3400', 'real_off_3400', 3400), loop('ro_4200', 'real_off_4200', 4200), loop('ro_5000', 'real_off_5000', 5000),
        loop('ro_5800', 'real_off_5800', 5800), loop('ro_6600', 'real_off_6600', 6600), loop('ro_7400', 'real_off_7400', 7400),
        loop('ro_8200', 'real_off_8200', 8200),
      ],
    },
    idleBed: { id: 'idle_bed', file: 'idle_bed' },
    starter: { id: 'starter', file: 'starter' },
    falsestarts: [
      { id: 'falsestart_1', file: 'falsestart_1' }, { id: 'falsestart_2', file: 'falsestart_2' }, { id: 'falsestart_3', file: 'falsestart_3' },
    ],
    shutoff: { id: 'shutoff', file: 'shutoff' },
    ambience: { id: 'ambience', file: 'ambience' },
    doorOpen: { id: 'door_open', file: 'door_open' },
    doorShut: { id: 'door_shut', file: 'door_shut' },
    // pit-box life while parked with the engine off (Marc drives THIS car — MIX2): Porsches passing outside,
    // the mechanics' wheel guns, the jack — same real clips as the GT3 / RSR (per-car dir: grants are per car)
    pitLife: [
      { id: 'pit_by_1', file: 'pit_by_1' }, { id: 'pit_by_2', file: 'pit_by_2' }, { id: 'pit_by_3', file: 'pit_by_3' }, { id: 'pit_by_4', file: 'pit_by_4' },
      { id: 'pit_wrench_1', file: 'pit_wrench_1' }, { id: 'pit_wrench_2', file: 'pit_wrench_2' }, { id: 'pit_wrench_3', file: 'pit_wrench_3' }, { id: 'pit_wrench_4', file: 'pit_wrench_4' },
      { id: 'pit_jack_1', file: 'pit_jack_1' }, { id: 'pit_jack_2', file: 'pit_jack_2' },
    ],
  },
};
